import { ServiceResponse } from '@nx-smartmonkey/shared/hooks';
import { useQuery } from '@tanstack/react-query';

import { getProjectGeoFenceMutationKey } from '.';
import { projectService } from '../../application/ProjectService';
import { ProjectActionType } from '../../context/project/actions';
import { useProjectContext } from '../../context/project/context';
import { GeoFence } from '../../domain/geoFence/GeoFence';
import useErrorMiddleware from '../errorMiddleware/useErrorMiddleware';
import { useShowSnackbarMessage } from '../snackbarMessage/useShowSnackbarMessage';
import { useRetrieveUserCredentials } from '../user/useRetrieveUserCredentials';
import { useRetrieveActiveProject } from './useRetrieveActiveProject';

interface UseRetrieveProjectGeoFenceProps {
  showErrorMessage?: boolean;
  geoFenceId?: string;
}

type UseRetrieveProjectGeoFenceResponse = ServiceResponse<Function, GeoFence>;

// Hooks act as 'Adapter' layer.
export const useRetrieveProjectGeoFence = ({
  showErrorMessage = true,
  geoFenceId,
}: UseRetrieveProjectGeoFenceProps): UseRetrieveProjectGeoFenceResponse => {
  const { showSnackbarMessage } = useShowSnackbarMessage();
  const { projectState, projectDispatch } = useProjectContext();
  const { data: userCredentials } = useRetrieveUserCredentials();
  const { data: activeProject } = useRetrieveActiveProject();
  const { sessionExpiredLogout } = useErrorMiddleware();

  const {
    isInitialLoading: isLoading,
    refetch,
    isError,
    isSuccess,
  } = useQuery(
    [getProjectGeoFenceMutationKey(), geoFenceId],
    (): Promise<GeoFence> => projectService.fetchGeoFence({ geoFenceId: geoFenceId! }),
    {
      enabled: !!activeProject && userCredentials?.subscription.test(`planner.geofences`) && !!geoFenceId,
      retry: false,
      onSuccess: (_geoFence) => {
        // In case the fetched geoFence is already in the state, update it, otherwise add it.
        const geoFences = projectState.geoFences.find((geoFence) => geoFence.id === _geoFence.id)
          ? projectState.geoFences.map((geoFence) => (geoFence.id === _geoFence.id ? _geoFence : geoFence))
          : [...projectState.geoFences, _geoFence];

        projectDispatch({
          type: ProjectActionType.FETCH_PROJECT_GEOFENCES_SUCCESS,
          payload: { geoFences },
        });
      },
      onError: (err: any) => {
        console.info(`Planner Retrieve ProjectBase GeoFence Error: `, err?.response);

        if (err?.response?.status === 401) {
          sessionExpiredLogout();
          return;
        }

        if (showErrorMessage) {
          showSnackbarMessage({
            messageIntlId: `infoerror.${err?.response?.data?.messageId ?? `network_error`}`,
          });
        }
      },
    }
  );

  return {
    error: isError,
    isLoading,
    success: isSuccess,
    run: refetch,
    data: projectState.geoFences.find((geoFence) => geoFence.id === geoFenceId),
  };
};
